import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Wireframe gizmo for a collision-shape resource, drawn in the node's `debug_color` like
 * Godot's editor collision overlay. CollisionShape3D mounts it only when `showCollisions` is on.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { decodeBoxShape3D } from '../../../../resources/shapes/boxshape3d';
import { decodeConvexPolygonShape3D } from '../../../../resources/shapes/convexpolygonshape3d';
import { decodeConcavePolygonShape3D } from '../../../../resources/shapes/concavepolygonshape3d';
import { decodeCapsuleShape3D } from '../../../../resources/shapes/capsuleshape3d';
import { decodeSphereShape3D } from '../../../../resources/shapes/sphereshape3d';
import { decodeCylinderShape3D } from '../../../../resources/shapes/cylindershape3d';
import { shapeDebugLines } from '../../shared/shapeDebugLines';
import { GizmoLine } from '../../../../r3f/components/GizmoLine';
import { warn } from '../../../../logger';
import { wireGizmoProgram } from '../../../../r3f/components/wireGizmoProgram';
import { useDisposeOnRelease } from '../../../../r3f/useDisposeOnRelease';
import { memoisedImport } from '../../../../resources/memoisedImport';
import { useLazyModule } from '../../../../resources/useLazyModule';
export function CollisionGizmo({ shape, color }) {
    const data = shape.data;
    const wire = wireGizmoProgram(color);
    switch (shape.type) {
        case 'BoxShape3D': {
            const { size } = decodeBoxShape3D(data);
            return (_jsxs("mesh", { children: [_jsx("boxGeometry", { args: [size.x, size.y, size.z] }), _jsx("meshBasicMaterial", { ...wire.props }, wire.key)] }));
        }
        case 'CapsuleShape3D': {
            const { radius, height } = decodeCapsuleShape3D(data);
            return (_jsxs("mesh", { children: [_jsx("capsuleGeometry", { args: [radius, height - radius * 2, 4, 16] }), _jsx("meshBasicMaterial", { ...wire.props }, wire.key)] }));
        }
        case 'SphereShape3D':
            return (_jsxs("mesh", { children: [_jsx("sphereGeometry", { args: [decodeSphereShape3D(data).radius, 16, 12] }), _jsx("meshBasicMaterial", { ...wire.props }, wire.key)] }));
        case 'CylinderShape3D': {
            const { radius, height } = decodeCylinderShape3D(data);
            return (_jsxs("mesh", { children: [_jsx("cylinderGeometry", { args: [radius, radius, height, 16] }), _jsx("meshBasicMaterial", { ...wire.props }, wire.key)] }));
        }
        case 'WorldBoundaryShape3D':
            return _jsx(WorldBoundaryLines, { shape: shape, color: color });
        case 'ConcavePolygonShape3D':
            return _jsx(TriangleSoupWire, { shapeData: data, color: color });
        case 'ConvexPolygonShape3D':
            return _jsx(ConvexHullWire, { shapeData: data, color: color });
        default:
            warn(`[CollisionShape3D] Unsupported shape type "${shape.type}" — drawing a unit wireframe box.`);
            return (_jsxs("mesh", { children: [_jsx("boxGeometry", { args: [1, 1, 1] }), _jsx("meshBasicMaterial", { ...wire.props }, wire.key)] }));
    }
}
/** One triangle, three vertices of three floats each: the least a triangle soup can draw. */
const MIN_TRIANGLE_SOUP_FLOATS = 3 * 3;
/** A tetrahedron's four corners: the fewest points that enclose a volume. */
const MIN_CONVEX_HULL_POINTS = 4;
/** Godot draws a world boundary as lines, not a wireframe mesh: a square and a normal ray. */
function WorldBoundaryLines({ shape, color }) {
    const positions = useMemo(() => shapeDebugLines(shape), [shape]);
    return positions && _jsx(GizmoLine, { positions: positions, color: color });
}
/** A concave shape is already a triangle soup, bound as a non-indexed BufferGeometry. */
function TriangleSoupWire({ shapeData, color }) {
    const geometry = useMemo(() => {
        const { data } = decodeConcavePolygonShape3D(shapeData);
        if (data.length < MIN_TRIANGLE_SOUP_FLOATS)
            return null;
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(data, 3));
        geom.computeVertexNormals();
        return geom;
    }, [shapeData]);
    useDisposeOnRelease(geometry);
    const wire = wireGizmoProgram(color);
    if (!geometry)
        return null;
    return (_jsx("mesh", { geometry: geometry, children: _jsx("meshBasicMaterial", { ...wire.props }, wire.key) }));
}
/**
 * `ConvexGeometry` lives in three's examples bundle, so it loads on demand: it stays off the
 * static-paint closure and out of the VS Code webview budget until collisions show.
 */
const loadConvexGeometry = memoisedImport(() => import('three/addons/geometries/ConvexGeometry.js'));
/** Convex hull from a point cloud, through the lazy-loaded ConvexGeometry. */
function ConvexHullWire({ shapeData, color }) {
    const hullModule = useLazyModule(loadConvexGeometry);
    const geometry = useMemo(() => {
        if (!hullModule)
            return null;
        const { points } = decodeConvexPolygonShape3D(shapeData);
        const verts = [];
        for (let i = 0; i + 2 < points.length; i += 3) {
            verts.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
        }
        if (verts.length < MIN_CONVEX_HULL_POINTS)
            return null;
        return new hullModule.ConvexGeometry(verts);
    }, [hullModule, shapeData]);
    useDisposeOnRelease(geometry);
    const wire = wireGizmoProgram(color);
    if (!geometry)
        return null;
    return (_jsx("mesh", { geometry: geometry, children: _jsx("meshBasicMaterial", { ...wire.props }, wire.key) }));
}
//# sourceMappingURL=CollisionGizmo.js.map